import React, { useState } from "react";
import { Panel, PanelHeader } from "@/components/ui-bits";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Copy, ExternalLink, Code2, Globe, Webhook } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function CopyBlock({ label, code, testid }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-bold">{label}</div>
        <Button size="sm" variant="ghost" onClick={copy} className="h-7 text-[10px] uppercase tracking-[0.14em]">
          <Copy size={12} className="mr-1" /> {copied ? "Copied" : "Copy"}
        </Button>
      </div>
      <pre className="bg-black border border-zinc-800 rounded-sm p-3 text-xs font-mono-cmd text-volt overflow-x-auto whitespace-pre-wrap break-all">{code}</pre>
    </div>
  );
}

export default function WebsiteEmbedPage() {
  const embedUrl = `${API_URL}/embed/lead`;
  const iframe = `<iframe
  src="${embedUrl}"
  width="100%"
  height="900"
  style="border:none; max-width:640px; margin:0 auto; display:block;"
  loading="lazy"
  title="Request a Training Spot"></iframe>`;

  const apiEndpoint = `${API_URL}/api/public/leads`;
  const curlExample = `curl -X POST ${apiEndpoint} \\
  -H "Content-Type: application/json" \\
  -d '{
    "first_name": "Priya",
    "last_name": "Sharma",
    "phone": "+919876543210",
    "email": "priya@example.com",
    "athlete_profile": "Recreational Athlete — I train to stay fit and healthy",
    "heard_from": "Instagram",
    "programme_code": "STRENGTH",
    "programme_label": "Comprehensive Physical Preparedness (Strength & Conditioning)",
    "preferred_start_date": "2026-08-01",
    "branch_id": "<paste-a-branch-id>",
    "is_online": false,
    "preferred_time_slot": "07:30–08:30",
    "referred_by_phone": "9876543211",
    "notes": "Prefer morning sessions"
  }'`;

  const zohoWebhookInstructions = `1. In your Zoho form (nammaxfit1 → Reserve Your Spot):
   Settings → Integrations → Webhooks → Configure
2. URL: ${apiEndpoint}
3. Method: POST
4. Content-Type: application/json
5. Map Zoho fields to our JSON keys:
     First Name        → first_name         (required)
     Last Name         → last_name          (required)
     Email             → email              (required)
     Phone             → phone              (required, 10+ digits)
     Athlete Profile   → athlete_profile
     Heard From        → heard_from
     Programme         → programme_code     (STRENGTH | MMA | HYROX | STRENGTH_KIDS | SPORTS_PERF | REHAB | FAT_LOSS | AESTHETICS | PT | OTHER)
                      → programme_label    (human-readable text of the choice)
     Programme (Other) → programme_other
     Start Date        → preferred_start_date  (YYYY-MM-DD, required)
     Location          → branch_id           (branch UUID) OR set is_online=true / location_other for special
     Time Slot         → preferred_time_slot  (only if Cunningham Road)
     Notes             → notes
     Referred By       → referred_by_phone   (10 digits)
6. Save. New Zoho submissions will now appear in Sales CRM instantly.`;

  return (
    <div className="p-8 space-y-6 animate-fade-in-up" data-testid="website-embed-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-volt font-semibold">Marketing · Website</div>
        <h1 className="font-display text-5xl font-black uppercase tracking-tight mt-1">Website Lead Form</h1>
        <div className="text-zinc-500 text-sm mt-1">Embed the form on your website · Or point Zoho / any HTML form at our API. All leads land in Sales CRM as NEW.</div>
      </div>

      <Panel data-testid="preview-panel">
        <PanelHeader
          title="Live Preview"
          subtitle={embedUrl}
          action={
            <a href={embedUrl} target="_blank" rel="noreferrer"
               className="text-[10px] uppercase tracking-[0.16em] text-volt font-bold flex items-center gap-1 hover:underline"
               data-testid="open-preview">
              <ExternalLink size={12} /> Open
            </a>
          }
        />
        <div className="rounded-sm overflow-hidden border border-zinc-800">
          <iframe src={embedUrl} title="Lead form preview" className="w-full" style={{ height: 720, border: "none" }} data-testid="preview-iframe" />
        </div>
      </Panel>

      <Panel data-testid="iframe-panel">
        <PanelHeader title="Option A · iframe embed" subtitle="Paste this HTML anywhere on your website — done in 30 seconds" />
        <div className="flex items-center gap-2 mb-3 text-[10px] uppercase tracking-[0.16em] text-zinc-500">
          <Globe size={12} className="text-volt" /> Best for WordPress / Wix / Webflow / Squarespace / plain HTML sites
        </div>
        <CopyBlock label="HTML" code={iframe} testid="iframe-code" />
      </Panel>

      <Panel data-testid="api-panel">
        <PanelHeader title="Option B · Public API" subtitle="Point Zoho webhook or any custom form at this endpoint" />
        <div className="flex items-center gap-2 mb-3 text-[10px] uppercase tracking-[0.16em] text-zinc-500">
          <Code2 size={12} className="text-volt" /> No auth · CORS-open · Honeypot spam-protected
        </div>
        <CopyBlock label="Endpoint" code={apiEndpoint} testid="api-url" />
        <div className="mt-4"><CopyBlock label="cURL example" code={curlExample} testid="curl-code" /></div>
      </Panel>

      <Panel data-testid="zoho-panel">
        <PanelHeader title="Zoho → CRM Webhook Setup" subtitle="Keep your Zoho form. Just add a webhook that mirrors submissions here." />
        <div className="flex items-center gap-2 mb-3 text-[10px] uppercase tracking-[0.16em] text-zinc-500">
          <Webhook size={12} className="text-volt" /> Follow these steps once — leads flow automatically from now on
        </div>
        <CopyBlock label="Instructions" code={zohoWebhookInstructions} testid="zoho-instructions" />
      </Panel>
    </div>
  );
}
